import jsCookie from "js-cookie";
import router from "@/router";
import { ElMessageBox } from "element-plus";

let didForceRefreshOnReload = false;

const isReloadNavigation = (): boolean => {
  try {
    const entry = performance.getEntriesByType?.("navigation")?.[0] as any;
    if (entry?.type) return entry.type === "reload";
    return (performance as any)?.navigation?.type === 1;
  } catch {
    return false;
  }
};

/**
 * 处理用户登出
 * @param message 提示消息
 * @param isForceLogout 是否是被迫登出
 */
export const handleLogout = (
  message = "您已退出登录",
  isForceLogout = false
) => {
  // 清除登录信息
  jsCookie.remove("username");
  jsCookie.remove("userToken");

  if (isForceLogout) {
    ElMessageBox.alert(message, "提示", {
      confirmButtonText: "确定",
      callback: () => {
        router.replace("/login");
      },
    });
  } else {
    router.replace("/login");
  }
};

/**
 * 处理强制登出
 */
export const handleForceLogout = (
  message = "您的账号在其他终端登录，已被强制下线"
) => {
  handleLogout(message, true);
};

// ==================== 静默Token刷新机制 ====================

/**
 * 静默Token刷新管理器
 */
class SilentTokenRefresh {
  private refreshTimer: NodeJS.Timeout | null = null;
  private readonly TOKEN_REFRESH_INTERVAL = 10 * 60 * 1000; // 每10分钟检查一次
  private readonly REFRESH_THRESHOLD = 30 * 60 * 1000; // 剩余30分钟时开始刷新

  /**
   * 启动静默token刷新
   */
  public start(): void {
    this.stop(); // 确保没有重复的定时器

    this.refreshTimer = setInterval(async () => {
      await this.checkAndRefreshToken();
    }, this.TOKEN_REFRESH_INTERVAL);

    console.log("静默token刷新已启动");

    // 启动时立刻检查一次，确保页面刷新后不会等待下一个轮询周期
    const shouldForceRefresh = isReloadNavigation() && !didForceRefreshOnReload;
    didForceRefreshOnReload = didForceRefreshOnReload || shouldForceRefresh;
    void this.checkAndRefreshToken({ force: shouldForceRefresh });
  }

  /**
   * 停止静默token刷新
   */
  public stop(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
    console.log("静默token刷新已停止");
  }

  /**
   * 检查并刷新token
   */
  private async checkAndRefreshToken(options?: {
    force?: boolean;
  }): Promise<void> {
    try {
      const userToken = jsCookie.get("userToken");
      if (!userToken) {
        console.log("未找到token，跳过刷新检查");
        return;
      }

      if (options?.force) {
        await this.refreshToken();
        return;
      }

      // 解码JWT token检查剩余时间
      const tokenPayload = this.decodeJWTPayload(userToken);
      if (!tokenPayload || !tokenPayload.exp) {
        console.warn("无法解码token或获取过期时间");
        return;
      }

      const currentTime = Math.floor(Date.now() / 1000);
      const remainingTime = tokenPayload.exp - currentTime;

      console.log(`Token剩余时间: ${Math.floor(remainingTime / 60)}分钟`);

      // 如果剩余时间小于阈值，尝试刷新token
      if (remainingTime < this.REFRESH_THRESHOLD / 1000) {
        await this.refreshToken();
      }
    } catch (error) {
      console.error("Token刷新检查失败:", error);
    }
  }

  /**
   * 解码JWT payload
   */
  private decodeJWTPayload(token: string): any {
    try {
      const payloadPart = token.split(".")[1];
      if (!payloadPart) return null;

      let payload = payloadPart.replace(/-/g, "+").replace(/_/g, "/");
      payload += "=".repeat((4 - (payload.length % 4)) % 4);

      const decodedPayload = atob(payload);
      return JSON.parse(decodedPayload);
    } catch (error) {
      console.error("JWT解码失败:", error);
      return null;
    }
  }

  /**
   * 刷新token
   */
  private async refreshToken(): Promise<void> {
    try {
      console.log("开始静默刷新token...");

      // 构建刷新请求
      const refreshData = {
        refresh_token: jsCookie.get("userToken"), // 使用当前token作为refresh token
      };

      // 尝试调用刷新接口
      // 这里使用原登录接口尝试刷新，实际项目中应该有专门的refresh接口
      const response = await fetch("/api/v1/auth/refresh", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${refreshData.refresh_token}`,
        },
        body: JSON.stringify(refreshData),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.data?.token) {
          // 更新cookie中的token
          jsCookie.set("userToken", result.data.token, {
            expires: 1,
            path: "/",
          });
          console.log("Token刷新成功");

          // 通知websocketManager使用新token
          try {
            const { default: websocketManager } = await import(
              "@/services/websocketManager"
            );
            websocketManager.updateToken(result.data.token);
          } catch (wsError) {
            console.warn("WebSocket token更新失败:", wsError);
          }
        } else {
          throw new Error("刷新响应中未包含新token");
        }
      } else {
        // 刷新失败，尝试使用原登录接口刷新
        await this.fallbackRefresh();
      }
    } catch (error) {
      console.error("Token刷新失败:", error);
      // 刷新失败时的降级处理
      await this.fallbackRefresh();
    }
  }

  /**
   * 降级刷新方法 - 使用现有接口尝试获取新token
   */
  private async fallbackRefresh(): Promise<void> {
    try {
      const username = jsCookie.get("username");
      if (!username) {
        console.warn("未找到用户名，跳过降级刷新");
        return;
      }

      // 这里可以调用用户信息接口来验证token是否还有效
      // 如果有效，则延长cookie时间
      const response = await fetch("/api/v1/auth/userinfo", {
        method: "GET",
        headers: {
          Authorization: `Bearer ${jsCookie.get("userToken")}`,
        },
      });

      if (response.ok) {
        // 如果接口调用成功，说明token仍然有效，延长cookie时间
        const remainingToken = jsCookie.get("userToken");
        if (remainingToken) {
          jsCookie.set("userToken", remainingToken, { expires: 1, path: "/" });
          console.log("Token有效期已延长");
        }
      } else {
        console.warn("Token验证失败，用户可能需要重新登录");
      }
    } catch (error) {
      console.error("降级刷新失败:", error);
    }
  }
}

// 导出单例实例
const silentTokenRefresh = new SilentTokenRefresh();

/**
 * 启动静默token刷新
 */
export const startSilentTokenRefresh = (): void => {
  silentTokenRefresh.start();
};

/**
 * 停止静默token刷新
 */
export const stopSilentTokenRefresh = (): void => {
  silentTokenRefresh.stop();
};

/**
 * 智能登录检查 - 支持静默token刷新
 */
export const smartLoginCheck = (): boolean => {
  const userToken = jsCookie.get("userToken");

  // 如果没有token，直接拒绝
  if (!userToken) {
    ElMessageBox.alert("登录已过期，请重新登录", "提示", {
      confirmButtonText: "确定",
      callback: () => {
        router.push("/login");
      },
    });
    return false;
  }

  // 启动静默token刷新（如果还没有启动）
  startSilentTokenRefresh();

  return true;
};
